import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { MAX_PHOTO_BYTES, PHOTO_EXTENSIONS } from "@goalhorns/shared";
import { ApiError, apiRequest } from "../../lib/api";
import type { Media, Person } from "../../lib/api-types";
import { paths, mediaUploadQuery } from "../../lib/endpoints";
import { sha256Hex } from "../../lib/hash";
import { uploadWithProgress } from "../../lib/upload";
import { invalidateWorkspaceData } from "../workspace/invalidate";

/** File extension in lower case, or "" when there is none. */
export function fileExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot < 0 ? "" : name.slice(dot + 1).toLowerCase();
}

function isPhotoExtension(ext: string): boolean {
  return (PHOTO_EXTENSIONS as readonly string[]).includes(ext);
}

/**
 * Hashes the photo in the browser, uploads it with progress, then attaches it to the person.
 * Client-side refusals reuse the API's codes so the same messages apply.
 */
export function usePhotoUpload(workspaceId: string, personId: string) {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<number | null>(null);

  const mutation = useMutation({
    mutationFn: async (file: File) => {
      const ext = fileExtension(file.name);
      if (!isPhotoExtension(ext)) {
        throw new ApiError(0, "ext_not_allowed", "Unsupported photo type");
      }
      if (file.size > MAX_PHOTO_BYTES) {
        throw new ApiError(0, "too_large", "Photo is too large");
      }
      const sha256 = await sha256Hex(file);
      setProgress(0);
      const url = `${paths.media(workspaceId)}${mediaUploadQuery({ kind: "photo", ext, sha256 })}`;
      const uploaded = await uploadWithProgress<{ media: Media }>(url, file, setProgress);
      await apiRequest<Person>(paths.person(workspaceId, personId), {
        method: "PATCH",
        body: { photo_media_id: uploaded.media.id },
      });
    },
    onSettled: () => {
      setProgress(null);
      return invalidateWorkspaceData(queryClient, workspaceId);
    },
  });

  return {
    upload: (file: File) => mutation.mutate(file),
    progress,
    error: mutation.error,
    isPending: mutation.isPending,
  };
}
